import axios from 'axios';
import { auth } from '../config/firebase';
import {
  Ticket,
  Sale,
  DashboardStats,
  Report,
  ApiResponse,
  PaginationParams,
  FilterParams,
  User,
  Event,
  Order,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  transformRequest: [
    (data, headers) => {
      // Don't transform FormData
      if (data instanceof FormData) {
        return data;
      }

      // Special handling for sales endpoint to ensure date is properly formatted
      if (data && typeof data === 'object' && 'saleDate' in data) {
        // Ensure saleDate is a string in ISO format
        const transformedData = {
          ...data,
          saleDate:
            typeof data.saleDate === 'string'
              ? data.saleDate
              : new Date(data.saleDate).toISOString(),
        };
        return JSON.stringify(transformedData);
      }
      return JSON.stringify(data);
    },
  ],
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }

  // If sending FormData, remove the Content-Type header so axios can set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // Log the request data for debugging
  if (config.method === 'post' && config.url === '/sales') {
    console.log('Sales POST request data:', config.data);
    console.log('Request headers:', config.headers);
    console.log('Data type:', typeof config.data);
    if (typeof config.data === 'string') {
      console.log('Parsed data:', JSON.parse(config.data));
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    // console.log('API Response:', response.config.url, response.data);
    return response;
  },
  async (error) => {
    console.error(
      'API Error:',
      error.config?.url,
      error.response?.data || error.message
    );

    // Log detailed error information for debugging
    if (
      error.response?.status === 400 &&
      error.config?.url?.includes('/tickets')
    ) {
      console.error('Detailed ticket creation error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        requestData: error.config.data,
        headers: error.response.headers,
      });
    }

    // Only logout if 401 and not an auth endpoint
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes('/auth/')
    ) {
      // Try to refresh the Firebase token once
      if (!error.config._retry) {
        error.config._retry = true;

        const user = auth.currentUser;
        if (user) {
          try {
            const newToken = await user.getIdToken(true); // Force refresh
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return api(error.config);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }
      }

      // If still failing after retry, logout
      if (error.config._retry) {
        auth.signOut();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const userService = {
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  async updateUser(userId: string, data: Partial<User>): Promise<User> {
    const response = await api.put<User>(`/users/${userId}`, data);
    return response.data;
  },

  async syncEmails(): Promise<{
    emails: any[];
    count: number;
    accounts: string[];
    message: string;
  }> {
    const response = await api.post('/auth/google/fetch-emails', {});
    return response.data;
  },
};

export const ticketService = {
  async getAll(params?: PaginationParams & FilterParams): Promise<Ticket[]> {
    const response = await api.get<{ tickets: Ticket[]; total: number }>(
      '/tickets',
      { params }
    );
    return response.data.tickets;
  },

  async getById(id: string): Promise<Ticket> {
    const response = await api.get<Ticket>(`/tickets/${id}`);
    return response.data;
  },

  async create(ticket: Omit<Ticket, 'id'>): Promise<Ticket> {
    const response = await api.post<Ticket>('/tickets', ticket);
    return response.data;
  },

  async update(id: string, ticket: Partial<Ticket>): Promise<Ticket> {
    const response = await api.patch<Ticket>(`/tickets/${id}`, ticket);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/tickets/${id}`);
  },

  async searchByEvent(eventName: string): Promise<Ticket[]> {
    const response = await api.get<Ticket[]>(`/tickets/search`, {
      params: { eventName },
    });
    return response.data;
  },

  async getByEvent(eventId: string): Promise<Ticket[]> {
    const response = await api.get<Ticket[]>(`/tickets/by-event/${eventId}`);
    return response.data;
  },

  async getBulkStats(eventIds: string[]): Promise<{
    [eventId: string]: {
      inventory: number;
      assignedInventory: number;
      inventoryLeft: number;
    };
  }> {
    const response = await api.post<{
      [eventId: string]: {
        inventory: number;
        assignedInventory: number;
        inventoryLeft: number;
      };
    }>('/tickets/bulk-stats', eventIds);
    return response.data;
  },
};

export const saleService = {
  async getAll(params?: PaginationParams & FilterParams): Promise<Sale[]> {
    const response = await api.get<{ sales: Sale[]; total: number } | Sale[]>(
      '/sales',
      { params }
    );
    // Handle both array and object response formats
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && 'sales' in response.data) {
      return response.data.sales;
    }
    return [];
  },

  async getById(id: string): Promise<Sale> {
    const response = await api.get<Sale>(`/sales/${id}`);
    return response.data;
  },

  async create(saleData: {
    ticketIds?: string[];
    eventId?: string;
    quantity?: number;
    section?: string;
    category?: string;
    block?: string;
    row?: string;
    restrictions?: string;
    split?: string;
    buyerName: string;
    buyerEmail: string;
    buyerPhone: string;
    salePrice: number;
    paymentMethod: 'cash' | 'card' | 'bankTransfer' | 'other';
    saleDate?: string;
    notes?: string;
  }): Promise<Sale> {
    // Manually stringify and parse to ensure clean data
    const payload = JSON.parse(JSON.stringify(saleData));
    console.log('Final payload being sent:', payload);
    console.log('saleDate type:', typeof payload.saleDate);
    console.log('saleDate value:', payload.saleDate);

    const response = await api.post<Sale>('/sales', payload);
    return response.data;
  },

  async update(id: string, saleData: Partial<Sale>): Promise<Sale> {
    const response = await api.patch<Sale>(`/sales/${id}`, saleData);
    return response.data;
  },

  async cancel(id: string): Promise<Sale> {
    const response = await api.post<Sale>(`/sales/${id}/cancel`);
    return response.data;
  },

  async getByDateRange(startDate: Date, endDate: Date): Promise<Sale[]> {
    const response = await api.get<Sale[]>('/sales/by-date', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  async getBulkStats(
    eventIds: string[]
  ): Promise<{ [eventId: string]: { sold: number } }> {
    const response = await api.post<{ [eventId: string]: { sold: number } }>(
      '/sales/bulk-stats',
      eventIds
    );
    return response.data;
  },
};

export const dashboardService = {
  // The operator's overview: deliveries due in the next 7 days, real sales figures, upcoming games,
  // inbox and bot health. One call, everything read off the live collections.
  async getOverview(): Promise<import('../types').DashboardOverview> {
    const response = await api.get('/dashboard/overview', { params: { _t: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
    return response.data;
  },
  async getStats(eventId?: string): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/dashboard/stats', {
      params: {
        ...(eventId ? { eventId } : {}),
        _t: Date.now(), // Add timestamp to prevent caching
      },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response.data;
  },
};

export const reportService = {
  async generateSalesReport(dateRange: {
    startDate: Date;
    endDate: Date;
  }): Promise<Report> {
    const response = await api.post<Report>('/reports/sales', dateRange);
    return response.data;
  },

  async generateInventoryReport(): Promise<Report> {
    const response = await api.post<Report>('/reports/inventory');
    return response.data;
  },

  async generateProfitReport(dateRange: {
    startDate: Date;
    endDate: Date;
  }): Promise<Report> {
    const response = await api.post<Report>('/reports/profit', dateRange);
    return response.data;
  },

  async exportReport(
    reportId: string,
    format: 'csv' | 'excel' | 'pdf'
  ): Promise<Blob> {
    const response = await api.get(`/reports/${reportId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },
};

export const eventService = {
  createEvent: async (event: Partial<Event>): Promise<Event> => {
    const response = await api.post<Event>('/events', event);
    return response.data;
  },

  getAllEvents: async (): Promise<Event[]> => {
    const response = await api.get<Event[]>('/events');
    return response.data;
  },

  getActiveEvents: async (): Promise<Event[]> => {
    const response = await api.get<Event[]>('/events/active');
    return response.data;
  },

  getEvent: async (id: string): Promise<Event> => {
    const response = await api.get<Event>(`/events/${id}`);
    return response.data;
  },

  updateEvent: async (id: string, event: Partial<Event>): Promise<Event> => {
    const response = await api.patch<Event>(`/events/${id}`, event);
    return response.data;
  },

  deleteEvent: async (id: string): Promise<void> => {
    await api.delete(`/events/${id}`);
  },
};

export const authService = {
  register: async (userData: {
    email: string;
    firebaseUid: string;
    name?: string;
    phone?: string;
    role?: string;
  }) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  loginWithFirebase: async (firebaseToken: string) => {
    const response = await api.post('/auth/login', { firebaseToken });
    return response.data;
  },
};

export const stadiumService = {
  getAll: async () => {
    const response = await api.get('/stadiums');
    return response.data;
  },

  getByTeamId: async (teamId: number) => {
    const response = await api.get(`/stadiums/team/${teamId}`);
    return response.data;
  },

  getSectionsForStadium: async (stadiumName: string) => {
    const response = await api.get(
      `/stadiums/${encodeURIComponent(stadiumName)}/sections`
    );
    return response.data;
  },

  getStadiumData: async (stadiumName: string) => {
    const response = await api.get(
      `/stadiums/${encodeURIComponent(stadiumName)}/data`
    );
    return response.data;
  },
};

// Fulfillment service
export const fulfillmentService = {
  getAll: async () => {
    const response = await api.get('/fulfillment');
    return response.data;
  },

  linkSaleToInventory: async (
    saleId: string,
    ticketId: string,
    quantity: number
  ) => {
    const response = await api.post('/fulfillment/link', {
      saleId,
      ticketId,
      quantity,
    });
    return response.data;
  },

  unlinkSaleFromInventory: async (saleId: string, ticketId: string) => {
    const response = await api.delete(
      `/fulfillment/unlink/${saleId}/${ticketId}`
    );
    return response.data;
  },

  getSaleFulfillment: async (saleId: string) => {
    const response = await api.get(`/fulfillment/sale/${saleId}`);
    return response.data;
  },

  getTicketFulfillment: async (ticketId: string) => {
    const response = await api.get(`/fulfillment/ticket/${ticketId}`);
    return response.data;
  },
};

export const supplierService = {
  create: async (data: any) => {
    const response = await api.post('/suppliers', data);
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/suppliers');
    return response.data;
  },

  getAllWithBalance: async () => {
    const response = await api.get('/suppliers/with-balance');
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await api.get(`/suppliers/${id}/stats`);
    return response.data;
  },

  getTransactionHistory: async (id: string) => {
    const response = await api.get(`/suppliers/${id}/transactions`);
    return response.data;
  },

  getPaymentHistory: async (id: string) => {
    const response = await api.get(`/suppliers/${id}/payments`);
    return response.data;
  },

  getUnpaidTransactions: async (id: string) => {
    const response = await api.get(`/suppliers/${id}/unpaid-transactions`);
    return response.data;
  },

  createPayment: async (id: string, data: any) => {
    const response = await api.post(`/suppliers/${id}/payments`, data);
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.patch(`/suppliers/${id}`, data);
    return response.data;
  },

  adjustBalance: async (
    id: string,
    data: { manualAdjustment: number; note?: string }
  ) => {
    const response = await api.post(`/suppliers/${id}/adjust-balance`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/suppliers/${id}`);
    return response.data;
  },
};

export const orderService = {
  async getAll(): Promise<Order[]> {
    const response = await api.get<Order[]>('/orders');
    return response.data;
  },

  async getById(id: string): Promise<Order> {
    const response = await api.get<Order>(`/orders/${id}`);
    return response.data;
  },

  async create(order: Partial<Order>): Promise<Order> {
    const response = await api.post<Order>('/orders', order);
    return response.data;
  },

  async update(id: string, order: Partial<Order>): Promise<Order> {
    const response = await api.patch<Order>(`/orders/${id}`, order);
    return response.data;
  },

  // Reject a HelloTickets order: sets it REJECTED on Hello and moves it into rejected_orders.
  async reject(id: string): Promise<{ ok: boolean; orderId: string; hello: any }> {
    const response = await api.post(`/orders/${id}/reject`, {});
    return response.data;
  },

  async listRejected(): Promise<any[]> {
    const response = await api.get('/orders/rejected/list');
    return response.data;
  },

  // Walk a Hello order forward on the platform: 'confirm' (pending→confirmed) or 'deliver'
  // (confirmed→delivered). Server enforces the required current status.
  async helloStatus(id: string, action: 'confirm' | 'deliver'): Promise<{ ok: boolean; from?: string; to?: string; already?: boolean }> {
    const response = await api.post(`/orders/${id}/hello-status`, { action });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/orders/${id}`);
  },
};

export const whatsappService = {
  generateLinkingToken: async (): Promise<{
    success: boolean;
    token: string;
    expiresAt: string;
    message: string;
  }> => {
    const response = await api.post('/whatsapp-linking/generate-token');
    return response.data;
  },

  getWhatsAppStatus: async (): Promise<{
    success: boolean;
    isLinked: boolean;
    phoneNumber?: string;
    linkedAt?: string;
    hasActiveToken?: boolean;
    tokenExpiresAt?: string;
  }> => {
    const response = await api.get('/whatsapp-linking/status');
    return response.data;
  },

  unlinkWhatsApp: async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.post('/whatsapp-linking/unlink');
    return response.data;
  },
};

export default api;
